class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));

      this._lastValue = '';
      // Shared debounced handler — both 'input' and 'keyup' use the same one
      // so they can't double-fire within a single debounce window.
      const debouncedOnChange = debounce(() => {
        const currentValue = this.input.value;
        if (currentValue !== this._lastValue) {
          this._lastValue = currentValue;
          this.onChange();
        }
      }, 300);

      // 'input' fires on every character in Chrome/Firefox and modern Safari
      this.input.addEventListener('input', debouncedOnChange);

      // 'keyup' fallback: catches cases where 'input' doesn't fire on
      // type="search" inside programmatically-opened <details> modals on iOS Safari.
      // Using the same debounced fn means only one call fires per keystroke.
      this.input.addEventListener('keyup', debouncedOnChange);

      // Compositionend covers CJK/IME input on all browsers
      this.input.addEventListener('compositionend', debouncedOnChange);

      // iOS Safari fix: the virtual keyboard suggestion bar and autocomplete
      // selections don't always fire 'input' or 'keyup' on type="search" inputs
      // inside a programmatically-opened modal. Poll the value on touchend as a
      // final safety net — the 50ms delay gives the DOM time to reflect the new value.
      this.input.addEventListener('touchend', () => {
        setTimeout(() => {
          const currentValue = this.input.value;
          if (currentValue !== this._lastValue) {
            this._lastValue = currentValue;
            this.onChange();
          }
        }, 50);
      });
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this._lastValue = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);
